import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Stack,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import { IniInfo } from "system/types/interfaces";
import { ApplicabilityRuleBuilder } from "../../subjectViewPage/applicabilityTab/ApplicabilityRuleBuilder";
import { closestCenter, DndContext, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, horizontalListSortingStrategy } from "@dnd-kit/sortable";
import ActiveDirectoryOUSortableChip from "./ActiveDirectoryOUSortableChip";

interface Segment {
  type: "OU" | "DC";
  value: string;
}

interface ActiveDirectoryOUModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: IniInfo) => void;
  initialOU: IniInfo | null;
  initialComment: IniInfo | null;
  mode: "add" | "edit";
}

const ActiveDirectoryOUModal: React.FC<ActiveDirectoryOUModalProps> = ({
  open,
  onClose,
  onSubmit,
  initialOU,
  initialComment,
  mode,
}) => {
  // Freeze the mode while the modal is open so the parent clearing initialComment
  // during close does not flash the Rule mode during the Dialog fade-out.
  const [isCommentMode, setIsCommentMode] = useState(false);

  useEffect(() => {
    if (open) setIsCommentMode(initialComment !== null);
  }, [open, initialComment]);

  const [segments, setSegments] = useState<Segment[]>([]);
  const [newType, setNewType] = useState<"OU" | "DC">("OU");
  const [newValue, setNewValue] = useState("");
  const [applicability, setApplicability] = useState("");
  const [delimiter, setDelimiter] = useState("is for");
  const [comment, setComment] = useState("");
  const [isValidRule, setIsValidRule] = useState(true);
  const [commentText, setCommentText] = useState("");

  const hasSegments = segments.length > 0;
  const canSave = isValidRule && hasSegments;

  useEffect(() => {
    if (isCommentMode && initialComment) {
      setCommentText((initialComment.value || "").replace(/^;\s*/, ""));
      return;
    }

    if (initialOU && initialOU.name && initialOU.name !== '""') {
      let dn = initialOU.name.trim();

      if (dn.startsWith('"') && dn.endsWith('"')) {
        dn = dn.slice(1, -1);
      }
      const parts = dn.split(",").map((part) => {
        const [type, ...valueArr] = part.split("=");
        return { type: type as "OU" | "DC", value: valueArr.join("=") };
      });
      setSegments(parts);
      setApplicability(initialOU.value || "");
      setDelimiter(initialOU.delimiter || "is for any");
      setComment(initialOU.inlineComment || "");
    } else {
      setSegments([]);
      setApplicability("");
      setDelimiter("is for any");
      setComment("");
      setCommentText("");
    }
  }, [initialOU, initialComment, isCommentMode, open]);

  const finalDN = useMemo(() => {
    const validSegments = segments.filter((s): s is Segment => !!s && typeof s === "object" && "type" in s && "value" in s);

    return validSegments.length > 0 ? `"${validSegments.map((s) => `${s.type}=${s.value}`).join(",")}"` : '""';
  }, [segments]);

  const addSegment = () => {
    if (!newValue.trim()) return;

    const newSegment: Segment = { type: newType, value: newValue.trim() };

    const newSegments = [...segments, newSegment];

    setSegments(newSegments);
    setNewValue("");
  };

  const removeSegment = (index: number) => {
    console.log("index", index);
    console.log("segments", segments);
    setSegments(segments.filter((_, i) => i !== index));
    setTimeout(() => {
      console.log("segments after", segments);
    }, 2000);
  };

  const handleBuilderChange = (result: any) => {
    setIsValidRule(result.isValid);
    setApplicability(result.finalValue);
    setDelimiter(result.finalDelimiter);
    setComment(result.comment);
  };

  const handleSubmit = () => {
    if (isCommentMode && initialComment) {
      const trimmed = commentText.trim();
      if (!trimmed) return;
      const finalValue = trimmed.startsWith(";") ? trimmed : `; ${trimmed}`;
      onSubmit({
        ...initialComment,
        value: finalValue,
        delimiter: "",
        inlineComment: "",
        isComment: true,
      });
      onClose();
      return;
    }

    if (!isValidRule) return;

    onSubmit({
      id: initialOU?.id || Date.now().toString(),
      name: finalDN,
      value: applicability,
      delimiter,
      inlineComment: comment,
      pendingChanges: true,
      isDeleted: false,
      isComment: false,
    });
    onClose();
  };

  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = parseInt(active.id as string);
    const newIndex = parseInt(over.id as string);

    setSegments((items) => arrayMove(items, oldIndex, newIndex));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ backgroundColor: "#1b1a19", color: "#fff" }}>
        <Typography variant="h6" fontWeight="bold">
          {isCommentMode ? "Edit Comment" : mode === "add" ? "Add Active Directory OUs" : "Edit Active Directory OUs"}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: "#1b1a19", pt: 2 }}>
        {isCommentMode ? (
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Comment"
              multiline
              rows={7}
              placeholder="Add your comment here"
              value={commentText}
              inputProps={{ style: { color: "#4caf50" } }}
              onChange={(e) => setCommentText(e.target.value)}
            />
          </Box>
        ) : (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Distinguished Name Builder
          </Typography>

          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={segments.map((_, i) => i.toString())} strategy={horizontalListSortingStrategy}>
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 3 }}>
                {segments.map((seg, i) => (
                  <ActiveDirectoryOUSortableChip key={i} segment={seg} index={i} onDelete={() => removeSegment(i)} />
                ))}
              </Stack>
            </SortableContext>
          </DndContext>

          <Box sx={{ display: "flex", gap: 2, mb: 3, alignItems: "flex-end" }}>
            <FormControl sx={{ minWidth: 100 }}>
              <InputLabel>Type</InputLabel>
              <Select value={newType} label="Type" onChange={(e) => setNewType(e.target.value as "OU" | "DC")}>
                <MenuItem value="OU">OU</MenuItem>
                <MenuItem value="DC">DC</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Value"
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addSegment()}
              placeholder="e.g. Workstations, Toronto, internal, com"
              sx={{ flex: 1 }}
            />

            <Button variant="contained" onClick={addSegment} disabled={!newValue.trim()}>
              Add Segment
            </Button>
          </Box>

          <TextField
            label="Final Distinguished Name (saved in Policy file)"
            value={finalDN}
            fullWidth
            disabled
            hidden
            helperText="leave blank for no OU selection"
            sx={{ mb: 3, fontFamily: "monospace" }}
          />

          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Applicability Rule
            </Typography>
            <ApplicabilityRuleBuilder
              initialName={finalDN === '""' ? "" : finalDN}
              initialDelimiter={delimiter}
              initialValue={applicability}
              initialComment={comment}
              isUpdating={true}
              onChange={handleBuilderChange}
              shouldDisplayNameValue={true}
            />
          </Box>
        </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ backgroundColor: "#1b1a19", p: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isCommentMode ? !commentText.trim() : !canSave}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { ActiveDirectoryOUModal };
